import { Link, NavLink, useLocation } from "react-router-dom";
import { Leaf, Menu, X, Instagram, Mail, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const navItems = [
  { to: "/", label: "Início", end: true },
  { to: "/beneficios", label: "Benefícios" },
  { to: "/adesao", label: "Adesão" },
  { to: "/contato", label: "Contato" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-white text-[#052E16] flex flex-col">
      <header className="sticky top-0 z-50 glass-nav" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#166534] text-white">
              <Leaf className="w-5 h-5" strokeWidth={2.2} />
            </span>
            <span className="font-display font-extrabold text-lg leading-none">
              Ressignificar
              <span className="block text-[11px] font-medium tracking-[0.18em] text-[#166534] mt-0.5">
                BENEFÍCIOS
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-[#166534]" : "text-neutral-700 hover:text-[#166534]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user?.role === "member" ? (
              <Link to="/painel">
                <Button
                  data-testid="header-member-area"
                  className="rounded-full bg-[#166534] hover:bg-[#14532d] hover:text-white text-white px-5"
                >
                  Área do Associado
                </Button>
              </Link>
            ) : user?.role === "admin" ? (
              <Link to="/admin">
                <Button
                  data-testid="header-admin-area"
                  className="rounded-full bg-[#166534] hover:bg-[#14532d] hover:text-white text-white px-5"
                >
                  Painel Admin
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/entrar">
                  <Button
                    variant="ghost"
                    data-testid="header-login"
                    className="rounded-full hover:bg-[#F0FDF4] hover:text-[#166534]"
                  >
                    Entrar
                  </Button>
                </Link>
                <Link to="/adesao">
                  <Button
                    data-testid="header-signup"
                    className="rounded-full bg-[#166534] hover:bg-[#14532d] hover:text-white text-white px-5"
                  >
                    Fazer Adesão
                  </Button>
                </Link>
              </>
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-neutral-200"
            aria-label="Abrir menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-neutral-100 bg-white" data-testid="mobile-menu">
            <div className="px-5 py-4 flex flex-col gap-3">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-2 text-base font-medium ${isActive ? "text-[#166534]" : "text-neutral-700"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-2 flex gap-2">
                {user ? (
                  <Link
                    to={user.role === "admin" ? "/admin" : "/painel"}
                    className="flex-1"
                    onClick={() => setOpen(false)}
                  >
                    <Button className="w-full rounded-full bg-[#166534] text-white hover:bg-[#14532d] hover:text-white">
                      {user.role === "admin" ? "Painel Admin" : "Área do Associado"}
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/entrar" className="flex-1" onClick={() => setOpen(false)}>
                      <Button variant="outline" className="w-full rounded-full">
                        Entrar
                      </Button>
                    </Link>
                    <Link to="/adesao" className="flex-1" onClick={() => setOpen(false)}>
                      <Button className="w-full rounded-full bg-[#166534] text-white hover:bg-[#14532d] hover:text-white">
                        Fazer Adesão
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" key={loc.pathname}>
        {children}
      </main>

      <footer className="border-t border-neutral-100 bg-[#F8FAF8]" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#166534] text-white">
                <Leaf className="w-4 h-4" />
              </span>
              <span className="font-display font-extrabold">Ressignificar Benefícios</span>
            </div>
            <p className="text-sm text-neutral-600 max-w-md">
              Um programa de benefícios acessível para toda a família. Mensalidade de R$ 30,00.
              Consultas cobradas separadamente.
            </p>
            <ul className="mt-4 space-y-2 text-sm" data-testid="footer-contacts">
              <li>
                <a href="https://instagram.com/ressignificarbeneficios" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-neutral-700 hover:text-[#166534]" data-testid="footer-instagram">
                  <Instagram className="w-4 h-4" /> @ressignificarbeneficios
                </a>
              </li>
              <li>
                <a href="mailto:ressignificarbeneficios@gmail.com" className="inline-flex items-center gap-2 text-neutral-700 hover:text-[#166534] break-all" data-testid="footer-email">
                  <Mail className="w-4 h-4 shrink-0" /> ressignificarbeneficios@gmail.com
                </a>
              </li>
              <li>
                <a href="https://wa.me/5531975548913" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-neutral-700 hover:text-[#166534]" data-testid="footer-whatsapp">
                  <MessageCircle className="w-4 h-4" /> (31) 97554-8913
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-3">Navegação</h4>
            <ul className="space-y-2 text-sm text-neutral-600">
              {navItems.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-[#166534]">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-3">Importante</h4>
            <p className="text-xs text-neutral-500 leading-relaxed">
              Não é plano de saúde, não é convênio médico e não oferece cobertura de procedimentos.
              As consultas são cobradas separadamente.
            </p>
          </div>
        </div>
        <div className="border-t border-neutral-100">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 text-xs text-neutral-500 flex items-center justify-between">
            <span>© {new Date().getFullYear()} Ressignificar Benefícios</span>
            <Link to="/admin/login" className="hover:text-[#166534]" data-testid="footer-admin-link">
              Acesso Administrativo
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
